import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/Button";
import { Feather } from "@expo/vector-icons";
export type { FieldRejectionInfo } from "@/lib/api/review";
export type { FieldEditInfo } from "@/lib/api/review";
export { formatRelativeTime, formatAbsoluteDate } from "@/lib/dateFormat";

// ─── Types ────────────────────────────────────────────────────────────────────

export type MainTab = "submitted_information" | "activity_log";
export type SubTab =
  | "legal_representative"
  | "business_information"
  | "product_compliance"
  | "financial_settlement"
  | "branding_localization";

// ─── DetailField ──────────────────────────────────────────────────────────────

interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

export function DetailField({ label, children }: DetailFieldProps) {
  return (
    <View className="gap-1 mb-8 items-start w-full">
      <AppText weight="medium" className="text-sm text-supporting-text uppercase tracking-wider">
        {label}
      </AppText>
      {children}
    </View>
  );
}

// ─── FieldRejectedBadge ───────────────────────────────────────────────────────

interface FieldRejectedBadgeProps {
  onShowReason: () => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

/** Shows a "Rejected" pill and "Show Reason" link for a field in the detail view. */
export function FieldRejectedBadge({ onShowReason, t }: FieldRejectedBadgeProps) {
  return (
    <View className="flex-row flex-wrap gap-3 items-center mt-2">
      <Button
        label="review.review.fieldState.rejected"
        onPress={() => {}}
        variant="destructive"
        activeOpacity={1}
      />
      <Button
        label="review.review.fieldState.showReason"
        onPress={onShowReason}
        variant="secondary"
        labelClassName="text-supporting-text"
        icon={<Icon as={Feather} name="eye" size={14} className="text-supporting-text" />}
      />
    </View>
  );
}

// ─── FieldEditedBadge ─────────────────────────────────────────────────────────

interface FieldEditedBadgeProps {
  onShowEdit: () => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

/** Shows an "Edited" pill and "Show Edit" link for a field that has been edited. */
export function FieldEditedBadge({ onShowEdit, t }: FieldEditedBadgeProps) {
  return (
    <View className="flex-row flex-wrap gap-3 items-center mt-2">
      <Button
        label="review.review.fieldState.edited"
        onPress={() => {}}
        variant="blue"
        activeOpacity={1}
      />
      <Button
        label="review.review.fieldState.showEdit"
        onPress={onShowEdit}
        variant="secondary"
        labelClassName="text-supporting-text"
        icon={<Icon as={Feather} name="eye" size={14} className="text-supporting-text" />}
      />
    </View>
  );
}
